<template>
  <!-- Remplacement de font-light par font-sans et couleurs adaptatives -->
  <div class="px-6 py-6 space-y-6 min-h-screen select-none text-left transition-colors duration-200 font-sans"
    :class="isDark ? 'bg-zinc-950 text-zinc-400' : 'bg-zinc-50 text-zinc-600'">

    <!-- En-tête de page humanisé -->
    <div class="flex sm:flex-row justify-between items-start sm:items-center border-b pb-5 gap-4 transition-colors duration-200"
      :class="isDark ? 'border-zinc-900' : 'border-zinc-200'">
      <div class="space-y-0.5">
        <h1 class="text-xl font-semibold tracking-tight transition-colors duration-200"
          :class="isDark ? 'text-white' : 'text-zinc-900'">Historique des Rendez-vous</h1>
        <p class="text-xs font-medium transition-colors duration-200"
          :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">Suivi des prestations, acomptes versés et affectations de l'équipe.</p>
      </div>

      <!-- Zone de Filtres épurée -->
      <div class="flex flex-wrap items-center gap-3 sm:w-auto w-full">
        <div class="relative flex-1 sm:flex-none">
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Rechercher un client, un soin..."
            class="w-full sm:w-64 px-4 py-2 rounded-xl text-xs transition-all focus:outline-none border"
            :class="isDark 
              ? 'bg-zinc-900/40 border-zinc-900 text-white placeholder-zinc-600 focus:border-zinc-700' 
              : 'bg-white border-zinc-200 text-zinc-900 placeholder-zinc-400 shadow-sm focus:border-zinc-400'"
          />
        </div>
        <select
          v-model="statusFilter"
          class="rounded-xl px-3 py-2 text-xs font-medium focus:outline-none cursor-pointer border transition-all"
          :class="isDark 
            ? 'bg-zinc-900/40 border-zinc-900 text-zinc-400 focus:border-zinc-700' 
            : 'bg-white border-zinc-200 text-zinc-600 shadow-sm focus:border-zinc-400'"
        >
          <option value="all">Tous les statuts</option>
          <option value="Validé">Validés</option>
          <option value="En attente">En attente</option>
          <option value="Annulé">Annulés</option>
          <option value="Terminé">Terminés</option>
        </select>
      </div>
    </div>

    <!-- État de chargement -->
    <div v-if="isLoading" class="flex justify-center items-center py-20">
      <div class="text-sm font-medium animate-pulse" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
        Chargement des rendez-vous...
      </div>
    </div>

    <!-- Tableau Premium Style Planity -->
    <div v-else class="overflow-x-auto rounded-2xl border no-scrollbar transition-all duration-200"
      :class="isDark ? 'bg-zinc-900/20 border-zinc-900 shadow-2xl shadow-black/40' : 'bg-white border-zinc-200/80 shadow-sm'">
      <table class="min-w-full divide-y" :class="isDark ? 'divide-zinc-900/60' : 'divide-zinc-100'">
        <thead class="text-[11px] font-semibold tracking-wide border-b transition-colors duration-200"
          :class="isDark ? 'bg-zinc-950/80 text-zinc-500 border-zinc-900' : 'bg-zinc-50 text-zinc-400 border-zinc-200'">
          <tr>
            <th class="px-6 py-4 font-semibold text-left">Date & Horaire</th>
            <th class="px-6 py-4 font-semibold text-left">Client</th>
            <th class="px-6 py-4 font-semibold text-left">Prestation</th>
            <th class="px-6 py-4 font-semibold text-left">Collaborateur</th>
            <th class="px-6 py-4 font-semibold text-left">Tarif</th>
            <th class="px-6 py-4 font-semibold text-center">Acompte</th>
            <th class="px-6 py-4 font-semibold text-left">Statut</th>
            <th class="px-6 py-4 font-semibold text-right pr-8">Actions</th>
          </tr>
        </thead>

        <tbody class="divide-y bg-transparent text-xs" :class="isDark ? 'divide-zinc-900/40' : 'divide-zinc-100'">
          <tr
            v-for="appointment in filteredAppointments"
            :key="appointment.id"
            class="group transition-colors duration-200"
            :class="isDark ? 'hover:bg-zinc-900/30' : 'hover:bg-zinc-50/60'"
          >
            <!-- Date & Horaire -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="font-semibold capitalize transition-colors duration-200" :class="isDark ? 'text-zinc-300' : 'text-zinc-800'">
                {{ formatDate(appointment.start) }}
              </div>
              <div class="text-[10px] font-medium mt-0.5 transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                {{ formatHourMinute(appointment.start) }} — {{ formatHourMinute(appointment.end) }}
              </div>
            </td>

            <!-- Client -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="font-semibold tracking-wide transition-colors duration-200" 
                :class="isDark ? 'text-white group-hover:text-zinc-300' : 'text-zinc-900 group-hover:text-zinc-700'">
                {{ appointment.nom }}
              </div>
              <div class="text-[10px] font-medium mt-0.5 transition-colors duration-200" :class="isDark ? 'text-zinc-500' : 'text-zinc-400'">
                {{ appointment.email }}
              </div>
            </td>

            <!-- Prestations (Raccourci) -->
            <td class="px-6 py-4 max-w-[180px] truncate">
              <div class="font-medium truncate transition-colors duration-200" :class="isDark ? 'text-zinc-300' : 'text-zinc-700'">
                {{ appointment.service.map(s => s.nom).join(', ') }}
              </div>
            </td>

            <!-- Collaborateur (Badge arrondi) -->
            <td class="px-6 py-4 whitespace-nowrap">
              <div class="flex items-center gap-2">
                <div class="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold border transition-colors duration-200"
                  :class="isDark ? 'bg-zinc-800 border-zinc-700 text-zinc-300' : 'bg-zinc-100 border-zinc-200 text-zinc-600'">
                  {{ appointment.employe?.nom?.charAt(0) || 'N' }}
                </div>
                <span class="font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-300' : 'text-zinc-600'">
                  {{ appointment.employe?.nom || 'Non assigné' }}
                </span>
              </div>
            </td>

            <!-- Prix -->
            <td class="px-6 py-4 font-semibold whitespace-nowrap transition-colors duration-200" :class="isDark ? 'text-white' : 'text-zinc-900'">
              {{ appointment.montant }} €
            </td>

            <!-- Acompte (Design pilule douce) -->
            <td class="px-6 py-4 text-center whitespace-nowrap">
              <span
                v-if="appointment.paymentAmount > 0"
                class="px-2.5 py-0.5 text-[10px] font-medium rounded-full border shadow-sm transition-colors duration-200"
                :class="isDark ? 'bg-amber-950/30 text-amber-400 border-amber-900/30' : 'bg-amber-50 text-amber-700 border-amber-200'"
              >
                {{ appointment.paymentAmount }} € Payés
              </span>
              <span
                v-else
                class="px-2.5 py-0.5 text-[10px] font-medium rounded-full border transition-colors duration-200"
                :class="isDark ? 'bg-zinc-900/50 border-zinc-800/60 text-zinc-500' : 'bg-zinc-100 border-zinc-200 text-zinc-400'"
              >
                Sur place
              </span>
            </td>

            <!-- Statut (Pills arrondies élégantes) -->
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="px-2.5 py-0.5 rounded-full text-[10px] font-medium tracking-wide border transition-colors duration-200"
                :class="[
                  appointment.status === 'Validé' || appointment.status === 'Confirmé' ? (isDark ? 'bg-emerald-950/40 text-emerald-400 border-emerald-900/40' : 'bg-emerald-50 text-emerald-700 border-emerald-200') :
                  appointment.status === 'En attente' ? (isDark ? 'bg-amber-950/40 text-amber-400 border-amber-900/40' : 'bg-amber-50 text-amber-700 border-amber-200') :
                  appointment.status === 'Terminé' ? (isDark ? 'bg-blue-950/40 text-blue-400 border-blue-900/40' : 'bg-blue-50 text-blue-700 border-blue-200') :
                  '(isDark ? bg-red-950/40 text-red-400 border-red-900/40 : bg-red-50 text-red-700 border-red-200)'
                ]"
              >
                {{ appointment.status }}
              </span>
            </td>

            <!-- Actions de ligne adaptatives -->
            <td class="px-6 py-4 whitespace-nowrap text-right pr-8">
              <div class="flex items-center justify-end gap-1.5">
                <button
                  @click="openDetails(appointment)"
                  class="p-2 rounded-xl border transition-all focus:outline-none cursor-pointer"
                  :class="isDark 
                    ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-400 hover:text-white border-zinc-800' 
                    : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 border-zinc-200/60 shadow-sm'"
                  title="Voir la fiche"
                >
                  <i class="fas fa-eye text-[11px]"></i>
                </button>
                <button
                  @click="editAppointment(appointment)"
                  class="p-2 rounded-xl border transition-all focus:outline-none cursor-pointer"
                  :class="isDark 
                    ? 'bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border-zinc-800' 
                    : 'bg-zinc-50 hover:bg-zinc-100 text-zinc-700 hover:text-zinc-900 border-zinc-200/60 shadow-sm'"
                  title="Modifier"
                >
                  <i class="fas fa-edit text-[11px]"></i>
                </button>
                <button
                  @click="deleteAppointment(appointment)"
                  class="p-2 rounded-xl border transition-all focus:outline-none cursor-pointer"
                  :class="isDark 
                    ? 'bg-zinc-900 hover:bg-red-950/60 text-red-400 border-zinc-800 hover:border-red-900' 
                    : 'bg-zinc-50 hover:bg-rose-50 text-rose-600 border-zinc-200/60 hover:border-rose-200 shadow-sm'"
                  title="Supprimer"
                >
                  <i class="fas fa-trash text-[11px]"></i>
                </button>
              </div>
            </td>
          </tr>

          <!-- État vide -->
          <tr v-if="filteredAppointments.length === 0 && !isLoading">
            <td colspan="8" class="text-center py-12 space-y-2">
              <svg class="w-6 h-6 mx-auto transition-colors duration-200" :class="isDark ? 'text-zinc-800' : 'text-zinc-300'" fill="none" stroke="currentColor" stroke-width="1.5" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
              <p class="text-xs font-medium transition-colors duration-200" :class="isDark ? 'text-zinc-600' : 'text-zinc-400'">
                Aucun rendez-vous trouvé pour les critères sélectionnés.
              </p>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import api from '@/api'

const router = useRouter()
const isLoading = ref(true)
const appointments = ref([])
const searchQuery = ref('')
const statusFilter = ref('all')
import { useTheme } from '@/composables/useTheme';

// Variable globale injectée ou reçue par props pour basculer de thème
const { isDark } = useTheme();
// Fetch appointments on mount
onMounted(async () => {
  await fetchAppointments()
})

const fetchAppointments = async () => {
  try {
    isLoading.value = true
    const response = await api.appointments.getAllAppointments({
      status: statusFilter.value !== 'all' ? statusFilter.value : undefined
    })
    
    appointments.value = response.data || []
    console.log(appointments)
  } catch (error) {
    console.error('Erreur lors de la récupération des rendez-vous:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la récupération des rendez-vous',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  } finally {
    isLoading.value = false
  }
}

// Business data query processing pipeline
const filteredAppointments = computed(() => {
  return appointments.value.filter(app => {
    const matchesSearch = !searchQuery.value ||
                          app.nom.toLowerCase().includes(searchQuery.value.toLowerCase()) ||
                          app.service.some(s => s.nom.toLowerCase().includes(searchQuery.value.toLowerCase())) ||
                          app.email.toLowerCase().includes(searchQuery.value.toLowerCase())
    const matchesStatus = statusFilter.value === 'all' || app.status === statusFilter.value
    return matchesSearch && matchesStatus
  })
})

// View appointment details
const openDetails = (appointment) => {
  // Naviguer vers la page d'édition du rendez-vous
  router.push(`/appointments/edit/${appointment._id || appointment.id}`)
}

// Edit appointment
const editAppointment = (appointment) => {
  router.push(`/appointments/new?id=${appointment.id}`)
}

// Delete appointment
const deleteAppointment = async (appointment) => {
  if (!confirm(`Êtes-vous sûr de vouloir supprimer le rendez-vous de ${appointment.nom} ?`)) {
    return
  }

  try {
    await api.appointments.deleteAppointment(appointment.id)

    toast.success('Rendez-vous supprimé avec succès', {
      position: 'top-right',
      autoClose: 2000
    })

    // Refresh the list
    await fetchAppointments()
  } catch (error) {
    console.error('Erreur lors de la suppression du rendez-vous:', error)
    toast.error(
      error.response?.data?.message || 'Erreur lors de la suppression',
      {
        position: 'top-right',
        autoClose: 3000
      }
    )
  }
}

// Data formatting utilities
const formatDate = (dateStr) => {
  return new Date(dateStr).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
}
const formatHourMinute = (dateStr) => {
  return new Date(dateStr).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
}
</script>

<style scoped>
/* Core micro system style resets */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}
.no-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
</style>
